import { ReactNode, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { ConstValues, createEnum } from '@cloud-wave/neon-common-lib';
import { RINGTONE_OPTIONS, RingtoneNames } from '@cloud-wave/neon-common-lib/app';

import { useConfigContext } from 'lib/core/config';

import { usePreferencesContext } from 'lib/common/contexts/PreferencesContext';

import { LogEvents, logger } from 'lib/common/components/LoggerController';

import getInstanceUrl from 'lib/common/utils/getInstanceUrl';

import { Context } from '.';

const DEFAULT_CCP_TIMEOUT_MS = 5000;
const LOG_LEVEL = 'ERROR';

const VDIPlatformType = createEnum('CITRIX', 'AWS_WORKSPACE', 'OMNISSA');
type TVDIPlatformType = ConstValues<typeof VDIPlatformType>;

// we include node types in our tsconfig, which is overriding the DOM type of MessageEvent,
// causing broken typing. unfortunate requirement for any
const omnissaTitleHandler = (event: any) => {
  if (event.data.type === 'get_horizon_window_title') {
    const ACTIVE_SOFTPHONE_TAB = 'Active Softphone Tab';

    let title = document.title;
    const currentTime = new Date();

    const titleSuffix = ` ${currentTime.getHours()}${currentTime.getMinutes()}${currentTime.getSeconds()} ${ACTIVE_SOFTPHONE_TAB}`;

    if (!title.endsWith(ACTIVE_SOFTPHONE_TAB)) {
      title += titleSuffix;
      document.title += titleSuffix;
    }

    event.source.postMessage({ type: 'horizon_window_title_response', title: title, source: 'parent' }, event.origin);
  }
};

function getCCPUrl(url: string) {
  return `${url}/ccp-v2`;
}

export default function ConnectStreams({ children }: { children: ReactNode }) {
  const {
    state: { ringtone },
  } = usePreferencesContext();
  const { config } = useConfigContext();

  const [initialized, setInitialized] = useState(false);
  const [failed, setFailed] = useState(false);

  const ccpEl = useRef(null);
  const instanceUrl = getInstanceUrl(config);

  const ccpLoginURL = config.SSO_URL || `${instanceUrl}/login`;

  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const vdiString = queryParams.get('vdi');
  const VDIPlatform = (Object.keys(VDIPlatformType).includes(vdiString ?? 'INVALID') ? vdiString : undefined) as
    | TVDIPlatformType
    | undefined;

  if (vdiString && !VDIPlatform) {
    logger.warn(LogEvents.INVALID_VDI_PARAMETER, { VDIPlatform: vdiString });
  }

  if (VDIPlatform === 'OMNISSA') {
    window.addEventListener('message', omnissaTitleHandler);
  }

  const initializeCCP = async () => {
    if (!ccpEl || !ccpEl.current) {
      return;
    }

    try {
      // This is used to ignore all [INFO] messages coming from CCP.
      // @ts-ignore
      connect.getLog().setEchoLevel(LOG_LEVEL);

      connect.core.initCCP(ccpEl.current, {
        ccpUrl: getCCPUrl(instanceUrl),
        loginPopup: true,
        loginUrl: ccpLoginURL,
        softphone: {
          allowFramedSoftphone: true,
          disableRingtone: ringtone === RingtoneNames.NO_RINGTONE,
          ringtoneUrl: RINGTONE_OPTIONS[ringtone].value,
          VDIPlatform,
        },
        task: {
          disableRingtone: ringtone === RingtoneNames.NO_RINGTONE,
          ringtoneUrl: RINGTONE_OPTIONS[ringtone].value,
        },
        chat: {
          disableRingtone: ringtone === RingtoneNames.NO_RINGTONE,
          ringtoneUrl: RINGTONE_OPTIONS[ringtone].value,
        },
        loginOptions: {
          autoClose: true,
          top: 0,
          left: 0,
          height: 472.867,
          width: 355,
          disableAuthPopupAfterLogout: Boolean(config.SSO_URL),
        },
        loginPopupAutoClose: true,
        region: config.CONNECT_REGION,
        ccpLoadTimeout: DEFAULT_CCP_TIMEOUT_MS,

        ...(config?.ACGR?.secondaryCCPUrl
          ? {
              // @ts-ignore Geniuses at AWS never updated the ts file for ACGR
              secondaryCCPUrl: getCCPUrl(config.ACGR.secondaryCCPUrl),
              enableGlobalResiliency: true,
            }
          : {}),
      });
    } catch (e) {
      logger.error(LogEvents.STREAMS_INIT.FAIL);
      setFailed(true);
      throw e;
    }

    return new Promise<void>((resolve) => {
      connect.core.onInitialized(() => {
        logger.info(LogEvents.STREAMS_INIT.SUCCESS);
        setInitialized(true);
        resolve();
      });
    });
  };

  return (
    <Context.Provider value={{ initializeCCP, initialized, failed }}>
      <div style={{ display: 'none' }} ref={ccpEl} />
      {children}
    </Context.Provider>
  );
}
